import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Shield, Church, User, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isSuper = params.get("super") === "1";

  // Radio-pill mode: "member" (default) vs "admin". Super-admin path is
  // orthogonal — driven purely by the ?super=1 query flag.
  const [mode, setMode] = useState(params.get("mode") === "admin" ? "admin" : "member");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Auto-detect the default church so we can hide the code field for
  // single-tenant setups. If /api/public/default-church returns a
  // church, we use its code silently. On failure (no default church
  // configured) the code field re-appears as a manual fallback.
  const [defaultChurch, setDefaultChurch] = useState(null);
  const [defaultLoading, setDefaultLoading] = useState(true);

  useEffect(() => {
    if (isSuper) { setDefaultLoading(false); return; }
    axios.get(`${API}/public/default-church`)
      .then((r) => {
        setDefaultChurch(r.data);
        if (r.data?.church_code) setCode(r.data.church_code);
      })
      .catch(() => setDefaultChurch(null))
      .finally(() => setDefaultLoading(false));
  }, [isSuper]);

  const hideCodeField = !isSuper && Boolean(defaultChurch?.church_code);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Defensive normalization: trim + lowercase the email so an iOS
      // AutoFill-inserted trailing space (or a stray uppercase first
      // letter that snuck through even with autoCapitalize="none")
      // still matches the record on the server. Password left as-is
      // — leading/trailing spaces are technically valid password
      // characters.
      const cleanEmail = (email || "").trim().toLowerCase();
      const data = await login(cleanEmail, password, isSuper ? undefined : code.toUpperCase());
      toast.success(t("welcomeBack"));
      if (data.user?.role === "super_admin") navigate("/super-admin");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-shell" data-testid="login-page">
      <div className="px-5 pt-6">
        <Link to="/" data-testid="login-back" className="inline-flex items-center gap-2 text-xs text-stone-500">
          <ArrowLeft className="w-3 h-3" /> {t("back")}
        </Link>
      </div>

      <div className="px-5 mt-6">
        <div className="w-12 h-12 rounded-xl grid place-items-center text-white"
             style={{ backgroundColor: isSuper ? "#0F172A" : "var(--tenant-primary)" }}>
          {isSuper ? <Shield className="w-5 h-5" /> : mode === "admin" ? <Crown className="w-5 h-5" /> : <Church className="w-5 h-5" />}
        </div>
        <h1 className="font-serif-display text-3xl mt-4 tracking-tight">
          {isSuper ? t("superAdmin") : (mode === "admin" ? t("adminSignIn") : t("memberSignIn"))}
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          {isSuper
            ? t("superAdminHelp")
            : mode === "admin"
              ? t("adminSignInHelp")
              : t("memberSignInHelp")}
        </p>
      </div>

      {!isSuper && (
        <div className="px-5 mt-5" data-testid="login-mode-pill">
          <div className="inline-flex w-full rounded-full bg-stone-100 p-1 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setMode("member")}
              data-testid="login-mode-member"
              className={`flex-1 rounded-full py-2 transition inline-flex items-center justify-center gap-1.5 ${
                mode === "member" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"
              }`}
            >
              <User className="w-3.5 h-3.5" /> {t("modeMember")}
            </button>
            <button
              type="button"
              onClick={() => setMode("admin")}
              data-testid="login-mode-admin"
              className={`flex-1 rounded-full py-2 transition inline-flex items-center justify-center gap-1.5 ${
                mode === "admin" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"
              }`}
            >
              <Crown className="w-3.5 h-3.5" /> {t("modeAdmin")}
            </button>
          </div>
        </div>
      )}

      <form className="px-5 mt-5 space-y-4" onSubmit={submit}>
        {!isSuper && !hideCodeField && !defaultLoading && (
          <div>
            <Label className="text-xs">{t("churchCode")}</Label>
            <Input
              data-testid="login-church-code"
              placeholder=""
              autoCapitalize="characters"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="bg-white tracking-widest font-semibold"
              maxLength={20}
            />
          </div>
        )}
        <div>
          <Label className="text-xs">{t("email")}</Label>
          <Input
            data-testid="login-email"
            type="email"
            autoComplete="email"
            // iOS Safari defaults `type="email"` inputs to
            // `autoCapitalize="sentences"` — capitalizing the first
            // letter of the address. Explicit `none` + autoCorrect off
            // stops the silent transformation that produces mismatches
            // for iPhone users.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white"
          />
        </div>
        <div>
          <Label className="text-xs">{t("password")}</Label>
          <PasswordInput
            data-testid="login-password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-white"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || defaultLoading}
          data-testid="login-submit"
          className="w-full rounded-full text-white h-11"
          style={{ backgroundColor: isSuper ? "#0F172A" : "var(--tenant-primary)" }}
        >
          {loading ? "…" : t("signIn")}
        </Button>

        <div className="text-center">
          <Link
            to={isSuper ? "/forgot-password?super=1" : "/forgot-password"}
            data-testid="link-forgot-password"
            className="text-xs text-stone-500 hover:text-stone-700 underline underline-offset-2"
          >
            Forgot password?
          </Link>
        </div>

        {!isSuper && mode === "member" && (
          <Link
            to="/register-member"
            data-testid="link-register-member"
            className="mt-5 flex items-center justify-between rounded-2xl border-2 border-dashed p-4 hover:bg-stone-50 transition"
            style={{ borderColor: "var(--tenant-primary)" }}
          >
            <div>
              <div className="font-serif-display text-base font-semibold tenant-text">
                {t("createAccount")}
              </div>
              <div className="text-xs text-stone-500 mt-0.5">{t("newMember")}</div>
            </div>
            <div
              className="w-9 h-9 rounded-full grid place-items-center text-white text-lg font-bold shrink-0"
              style={{ backgroundColor: "var(--tenant-primary)" }}
            >
              →
            </div>
          </Link>
        )}
      </form>
    </div>
  );
}
